/**
 * Shared payment event property shape for PostHog funnel + revenue analytics.
 * Used by portal (Guardian/Finix/PAC), staff routes, and webhooks.
 */
export type PaymentIntegration =
  | 'finix'
  | 'guardian_cardpointe'
  | 'isn_pac'
  | 'manual'
  | 'spectora';

export type PaymentFlow = 'portal' | 'staff' | 'webhook';

export interface PaymentEventProperties {
  inspection_id?: string;
  company_id?: string;
  payment_id?: string;
  refund_id?: string;
  payment_method?: string;
  payment_integration?: PaymentIntegration;
  payment_flow?: PaymentFlow;
  amount?: number;
  fee?: number;
  total?: number;
  /** When only cents are known, dollars are derived for `amount`. */
  amount_cents?: number;
  refund_amount?: number;
  payment_amount?: number;
  fully_refunded?: boolean;
  source?: string;
  type?: string;
  status?: string;
  transfer_state?: string;
  failure_code?: string;
  failure_message?: string;
  idempotent_replay?: boolean;
  payer_contact_id?: string;
  isn_order_id?: number | string;
}

function dollarsFromCents(cents: number): number {
  return Math.round(cents) / 100;
}

/** Normalize payment analytics props and attach company group when present. */
export function buildPaymentEventProperties(
  props: PaymentEventProperties
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined && value !== null && value !== '') {
      out[key] = value;
    }
  }

  if (out.amount == null && typeof props.amount_cents === 'number') {
    out.amount = dollarsFromCents(props.amount_cents);
  }

  if (
    out.total == null &&
    typeof out.amount === 'number' &&
    typeof out.fee === 'number'
  ) {
    out.total = Math.round((out.amount + out.fee) * 100) / 100;
  }

  const companyId =
    typeof props.company_id === 'string' ? props.company_id.trim() : '';
  if (companyId) {
    out.$groups = { company: companyId };
  }

  return out;
}
